import { Component, HostListener, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { TranslateModule } from '@ngx-translate/core';
import { AgentThreadListComponent } from '../../features/agent/thread-list.component';
import { ThreadSearchOverlayComponent } from '../../features/agent/thread-search-overlay.component';
import { ChatService } from '../../features/agent/agent.service';
import { SelectorOrganizacionComponent } from '../../shared/components/selector-organizacion/selector-organizacion.component';
import { SIN_ROL } from '../../shared/utils/nombre-visible';

export interface SidebarNavItem {
  icon: string;
  route?: string;
  label: string;
  active?: boolean;
  action?: () => void;
  count?: number;
  hidden?: boolean;
  meta?: string;
}

export interface SidebarNavSection {
  label: string;
  items: SidebarNavItem[];
}

export interface SidebarUser {
  name: string;
  nickname?: string;
  role?: string;
  avatar?: string;
  initials?: string;
  /** Fondo del avatar de iniciales (avatarGradientForSeed del email), el mismo que en Ajustes. */
  avatarBackground?: string;
}

export interface SidebarAppIcon {
  icon?: string;
  image?: string;
  route?: string;
  title?: string;
}

@Component({
  selector: 'app-sidebar-floating',
  standalone: true,
  imports: [CommonModule, RouterModule, OverlayBadgeModule, TranslateModule, AgentThreadListComponent, ThreadSearchOverlayComponent, SelectorOrganizacionComponent],
  templateUrl: './sidebar-floating.component.html',
  styleUrls: ['./sidebar-floating.component.scss']
})
export class SidebarFloatingComponent implements OnInit, OnChanges {
  @Input() sections: SidebarNavSection[] = [];
  @Input() navItems: SidebarNavItem[] = [];
  @Input() user: SidebarUser | null = null;
  @Input() appIcon: SidebarAppIcon | null = null;

  @Output() collapsedChange = new EventEmitter<boolean>();

  /** Mismo texto que la píldora de la lista de miembros, para que no vuelvan a divergir. */
  readonly SIN_ROL = SIN_ROL;

  visibleSections: SidebarNavSection[] = [];
  isCollapsed = false;

  isBottomSidebar = false;

  /** Paleta de búsqueda de conversaciones (lupa de la cabecera, solo /agent). */
  showThreadSearch = false;

  constructor(
    private readonly router: Router,
    private readonly chatService: ChatService
  ) {}

  ngOnInit(): void {
    this.updateSidebarMode();
    this.updateVisibleSections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections']) {
      this.updateVisibleSections();
    }
  }

  private updateVisibleSections(): void {
    this.visibleSections = this.sections.map(s => ({
      ...s,
      items: s.items.filter(i => !i.hidden)
    }));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateSidebarMode();
  }

  get otherNavItems(): SidebarNavItem[] {
    return this.navItems.filter(item => !item.hidden);
  }

  private updateSidebarMode(): void {
    if (globalThis.window === undefined) {
      this.isBottomSidebar = false;
      return;
    }
    this.isBottomSidebar = globalThis.window.innerWidth <= 1019;
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.collapsedChange.emit(this.isCollapsed);
  }

  toggleThreadSearch(): void {
    this.showThreadSearch = !this.showThreadSearch;
  }

  /** Turnos corriendo en conversaciones que NO se están viendo. */
  runningCount(): number {
    return this.chatService.runningElsewhereCount();
  }

  /**
   * [+] de la fila del Agente. NO crea el thread en el servidor (app#570):
   * limpia la conversación y el deep-link; el thread nace con el primer
   * mensaje.
   */
  startNewConversation(): void {
    this.chatService.startNewConversation();
    void this.router.navigate(['/agent']);
  }

  goToSettings(): void {
    this.router.navigate(['/settings']);
  }

}
